import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Button, HorizontalScroller } from 'components/ui'
import { FileTile } from 'components/ui/MediaGallery/FileTile'
import { getFileMetadata, isPreviewableImageFile, safeFileUrl } from 'components/UploadedFileList/fileMetadata'
import { Box, Text, Image, PseudoBox, Portal } from '@chakra-ui/core'
import { css } from '@emotion/core'
import FsLightbox from 'fslightbox-react'
import { VideoPreview } from 'components/CommonComponents/VideoPreview'
import useLocaleText from 'components/useLocaleText'
import {
  RichTextEditor,
  EditorContent,
  isEditorContentEmpty,
  textToEditorContent,
} from 'components/CommonComponents/RichTextEditor'

const TEXT_LINE_HEIGHT = 22
const TEXT_COLLAPSED_HEIGHT = TEXT_LINE_HEIGHT * 3.5
const TEXT_FADE_HEIGHT = TEXT_LINE_HEIGHT / 2

interface CollapsibleContentProps {
  images?: string[]
  videos?: string[]
  files?: string[]
  text?: string
  rich_text?: EditorContent | null
  onLightboxChange?: (isOpen: boolean) => void
  collapsedHeight?: number
  defaultExpanded?: boolean
  showHeader?: boolean
  headerTitle?: string
  variant?: 'default' | 'inset'
}

export const CollapsibleContent: React.FC<CollapsibleContentProps> = ({
  images,
  videos,
  files,
  text,
  rich_text,
  onLightboxChange,
  collapsedHeight = TEXT_COLLAPSED_HEIGHT,
  defaultExpanded = false,
  showHeader = true,
  headerTitle,
  variant = 'default',
}) => {
  const allMedia = useMemo(() => [...(images || []), ...(videos || [])], [images, videos])
  const imageFiles = useMemo(
    () => (files || []).filter((url) => safeFileUrl(url) && isPreviewableImageFile(getFileMetadata(url))),
    [files]
  )
  const lightboxMedia = useMemo(() => [...allMedia, ...imageFiles], [allMedia, imageFiles])

  const allFiles = files || []
  const hasRichText = !!(rich_text && !isEditorContentEmpty(rich_text))
  const hasText = !!text?.trim()
  const hasContent = allMedia.length > 0 || allFiles.length > 0 || hasRichText || hasText

  const editorValue = useMemo<EditorContent | null>(
    () => (hasRichText ? rich_text! : hasText ? textToEditorContent(text) : null),
    [hasRichText, rich_text, hasText, text]
  )

  const [lightboxToggler, setLightboxToggler] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [isTextOverflowing, setIsTextOverflowing] = useState(false)
  const [naturalTextHeight, setNaturalTextHeight] = useState(0)

  const textRef = useRef<HTMLDivElement>(null)

  const scrollText = useLocaleText({
    previous: { key: 'attachments_scroll_previous', fallback: { en: 'Previous attachments', he: 'הקבצים הקודמים' } },
    next: { key: 'attachments_scroll_next', fallback: { en: 'Next attachments', he: 'הקבצים הבאים' } },
  })
  const t_description = useLocaleText('description', true)
  const t_see_more = useLocaleText('see_more')
  const t_see_less = useLocaleText('show_less')

  const displayTitle = headerTitle || t_description || 'Description'
  const inset = variant === 'inset'

  const mediaTypes = useMemo<Array<'image' | 'video'>>(
    () => [
      ...(images || []).map(() => 'image' as const),
      ...(videos || []).map(() => 'video' as const),
      ...imageFiles.map(() => 'image' as const),
    ],
    [images, videos, imageFiles]
  )

  const handleImageClick = useCallback(
    (index: number, e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation()
        e.preventDefault()
      }
      setCurrentImageIndex(index)
      setLightboxToggler((prev) => !prev)
      onLightboxChange?.(true)
    },
    [onLightboxChange]
  )

  const handleLightboxClose = useCallback(() => {
    setTimeout(() => {
      onLightboxChange?.(false)
    }, 100)
  }, [onLightboxChange])

  const toggleExpand = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setIsExpanded((prev) => !prev)
  }, [])

  useEffect(() => {
    const el = textRef.current

    if (!el) return

    const check = () => {
      setNaturalTextHeight(el.scrollHeight)
      setIsTextOverflowing(el.scrollHeight > collapsedHeight)
    }

    const timer = setTimeout(check, 0)
    const observer = new ResizeObserver(check)

    observer.observe(el)

    return () => {
      clearTimeout(timer)
      observer.disconnect()
    }
  }, [text, rich_text, collapsedHeight])

  if (!hasContent) return null

  return (
    <>
      <Box w="100%" flexShrink={0}>
        {inset && showHeader && (
          <Text mb="10px" fontSize="13px" fontWeight="600" color="#454d57">
            {displayTitle}
          </Text>
        )}
        <Box
          bg={inset ? '#f5f6f8' : 'transparent'}
          w="100%"
          borderRadius={inset ? '14px' : '8px'}
          border={inset ? '0' : '1px solid #E5E7EB'}
          overflow="hidden"
          display="block"
          onClick={(e) => e.stopPropagation()}
        >
          {showHeader && !inset && (
            <Box px="10px" pt="10px" pb="6px">
              <Text fontSize="15px" fontWeight="700" color="#111827" letterSpacing="0.3px">
                {displayTitle}
              </Text>
            </Box>
          )}

          {/* Text section */}
          {editorValue && (
            <Box
              bg={inset ? 'transparent' : 'white'}
              px="16px"
              pt={inset ? '16px' : showHeader ? '2px' : '10px'}
              pb={inset && !isTextOverflowing ? '12px' : 0}
              position="relative"
            >
              {/* ref on the constrained box — scrollHeight gives full content height despite overflow:hidden */}
              <Box
                ref={textRef as any}
                style={{
                  maxHeight: isExpanded
                    ? `${naturalTextHeight || collapsedHeight}px`
                    : `${Math.min(naturalTextHeight || collapsedHeight, collapsedHeight)}px`,
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease',
                  ...(!isExpanded && isTextOverflowing
                    ? {
                        WebkitMaskImage: `linear-gradient(to bottom, #000 0, #000 calc(100% - ${TEXT_FADE_HEIGHT}px), transparent 100%)`,
                        maskImage: `linear-gradient(to bottom, #000 0, #000 calc(100% - ${TEXT_FADE_HEIGHT}px), transparent 100%)`,
                      }
                    : {}),
                }}
              >
                <Box
                  css={css`
                    .rich-text-editor-content {
                      min-height: 0 !important;
                      padding: 0 !important;
                      line-height: 22px;
                    }
                  `}
                >
                  <RichTextEditor
                    value={editorValue}
                    onChange={() => {}}
                    editable={false}
                    showToolbar={false}
                    containerStyle={{
                      border: 'none',
                      backgroundColor: 'transparent',
                      minHeight: 'auto',
                      padding: 0,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Read More / Read Less link */}
          {editorValue && isTextOverflowing && (
            <Box px="20px" pt="6px" pb={allMedia.length > 0 ? '6px' : '10px'}>
              <Button
                type="button"
                variant="ghost"
                height="auto"
                p={0}
                aria-expanded={isExpanded}
                fontSize="14px"
                fontWeight="600"
                color="#222222"
                cursor="pointer"
                display="inline"
                onClick={toggleExpand}
                _hover={{ bg: 'transparent', color: '#222222' }}
                _active={{ bg: 'transparent' }}
              >
                {isExpanded ? t_see_less || 'Read Less' : t_see_more || 'Read More'}
              </Button>
            </Box>
          )}

          {/* One shared row keeps reference files beside images and videos. */}
          {(allMedia.length > 0 || allFiles.length > 0) && (
            <Box px="20px" pb="12px" pt={editorValue ? '4px' : showHeader ? '2px' : '10px'}>
              <HorizontalScroller
                gap="8px"
                centered
                navigation={{ previousLabel: scrollText.previous, nextLabel: scrollText.next }}
              >
                {allMedia.map((mediaUrl, index) => (
                  <PseudoBox
                    key={index}
                    style={{ direction: 'ltr' }}
                    flexShrink={0}
                    w="100px"
                    h="100px"
                    borderRadius="16px"
                    overflow="hidden"
                    border="1px solid #e7e6e0"
                    bg="#FFFFFF"
                    position="relative"
                    _hover={{ transform: 'scale(1.02)' }}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    transition="all 0.2s"
                    cursor="pointer"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      handleImageClick(index, e)
                    }}
                  >
                    {mediaTypes[index] === 'video' ? (
                      <VideoPreview
                        openViewer={(e?: React.MouseEvent) => {
                          e?.stopPropagation()
                          handleImageClick(index, e)
                        }}
                        url={mediaUrl}
                      />
                    ) : (
                      <Image src={mediaUrl} alt={`Content media ${index + 1}`} w="100%" h="100%" objectFit="cover" />
                    )}
                  </PseudoBox>
                ))}
                {allFiles.map((fileUrl, index) => (
                  <FileTile
                    key={`${fileUrl}-${index}`}
                    url={fileUrl}
                    size={100}
                    onPreview={
                      imageFiles.includes(fileUrl)
                        ? () => handleImageClick(allMedia.length + imageFiles.indexOf(fileUrl))
                        : undefined
                    }
                  />
                ))}
              </HorizontalScroller>
            </Box>
          )}
        </Box>
      </Box>
      {lightboxMedia.length > 0 && (
        <Portal container={document.getElementById('root')}>
          <FsLightbox
            key={JSON.stringify(lightboxMedia)}
            toggler={lightboxToggler}
            sources={lightboxMedia}
            sourceIndex={currentImageIndex}
            types={mediaTypes}
            onClose={handleLightboxClose}
          />
        </Portal>
      )}
    </>
  )
}
